import { FileMap, FileUploadStatus, FileWithStatus } from '@/types/upload';
import { pluralize } from '@/utils/format';

export enum UploadStatusTabs {
  ALL = 'all',
  SUCCESS = 'success',
  ERROR = 'error',
  QUEUED = 'queued',
}

export type FolderGroup = {
  folderPath: string;
  files: FileWithStatus[];
  stats: {
    total: number;
    success: number;
    errors: number;
  };
};

export const categorizeFiles = (files: FileWithStatus[]) => {
  return {
    completed: files.filter(file => file.status.state === FileUploadStatus.COMPLETED),
    failed: files.filter(file => file.status.state === FileUploadStatus.FAILED),
    uploading: files.filter(file => file.status.state === FileUploadStatus.UPLOADING),
    queued: files.filter(
      file => file.status.state === FileUploadStatus.PENDING || file.status.state === FileUploadStatus.UPLOADING
    ),
    progressEligible: files.filter(
      file =>
        file.status.state === FileUploadStatus.UPLOADING ||
        file.status.state === FileUploadStatus.COMPLETED ||
        file.status.state === FileUploadStatus.FAILED
    ),
  };
};

export const getFilesForFilter = (
  filter: UploadStatusTabs,
  completedFiles: FileWithStatus[],
  failedFiles: FileWithStatus[],
  queuedFiles: FileWithStatus[]
): FileWithStatus[] => {
  if (filter === UploadStatusTabs.ALL) return [...completedFiles, ...failedFiles, ...queuedFiles];
  if (filter === UploadStatusTabs.SUCCESS) return completedFiles;
  if (filter === UploadStatusTabs.ERROR) return failedFiles;
  return queuedFiles;
};

export const getEmptyStateMessage = (filter: UploadStatusTabs): string => {
  if (filter === UploadStatusTabs.ALL) return 'No files to display';
  if (filter === UploadStatusTabs.SUCCESS) return 'No successful uploads yet';
  if (filter === UploadStatusTabs.ERROR) return 'No errors - all uploads successful!';
  return 'No files currently queued';
};

export const groupFilesByFolder = (files: FileWithStatus[]): FolderGroup[] => {
  const folderMap = new Map<string, FileWithStatus[]>();

  for (const file of files) {
    const pathParts = file.path.split('/');
    const folderPath = pathParts.length > 1 ? pathParts.slice(0, -1).join('/') + '/' : '';

    if (!folderMap.has(folderPath)) {
      folderMap.set(folderPath, []);
    }
    folderMap.get(folderPath)!.push(file);
  }

  const groups: FolderGroup[] = [];

  for (const [folderPath, files] of folderMap) {
    const successCount = files.filter(f => f.status.state === FileUploadStatus.COMPLETED).length;
    const errorCount = files.filter(f => f.status.state === FileUploadStatus.FAILED).length;

    groups.push({
      folderPath,
      files,
      stats: {
        total: files.length,
        success: successCount,
        errors: errorCount,
      },
    });
  }

  groups.sort((a, b) => a.folderPath.localeCompare(b.folderPath));

  return groups;
};

export const getFolderSummary = (stats: FolderGroup['stats']): string => {
  const parts: string[] = [];

  parts.push(pluralize(stats.total, 'file', 'files'));

  if (stats.errors > 0) {
    parts.push(pluralize(stats.errors, 'error', 'errors'));
  } else if (stats.success === stats.total) {
    parts.push('all successful');
  }

  return parts.join(', ');
};

export const getUploadFileCategories = (files: FileMap | FileWithStatus[]) => {
  const fileArray = Array.isArray(files) ? files : Object.values(files);
  return categorizeFiles(fileArray);
};

export const calculateUploadProgress = (
  progressEligibleFiles: FileWithStatus[],
  uploadProgress: Record<string, number>
) => {
  const totalProgress = progressEligibleFiles.reduce((sum, file) => {
    if (file.status.state === FileUploadStatus.COMPLETED || file.status.state === FileUploadStatus.FAILED) {
      return sum + 100;
    }
    return sum + (uploadProgress[file.path] || 0);
  }, 0);

  return progressEligibleFiles.length > 0 ? Math.round(totalProgress / progressEligibleFiles.length) : 0;
};
